/*
 * Dollars and credits.
 *
 * There were three USD formatters, and two of them were wrong: `WalletPage` and
 * `apiClient` used `toFixed(2)`, which does not group thousands, while
 * `topupAmount` used `toLocaleString`, which does. So the same amount rendered
 * as `$1000.00` on the wallet page and `$1,000.00` in the top-up validation
 * message — visible to any user buying more than $999 of credits, which is
 * exactly the customer you least want to show a sloppy number to.
 *
 * The grouped form wins.
 */

/** `$1,000.00`. Always two decimals — a price with one is a typo. */
export function formatUsd(amount: number): string {
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Unsigned credits: `100` for whole amounts, `16.10` otherwise, `10,000` once
 * there are thousands to group.
 *
 * No currency symbol, deliberately. The wallet counts credits, and although
 * $1 = 1 credit makes the numbers identical, writing `$` on a credit balance is
 * the mistake this codebase has already had to fix once.
 *
 * Grouped for the same reason `formatUsd` is, and it was not: this used
 * `String()`/`toFixed`, so the wallet page showed a balance of `10000` directly
 * above a top-up field that priced the same magnitude at `$10,000.00`.
 *
 * The decimals still differ from USD on purpose — a whole number of credits is
 * written whole, because `100.00 credits` reads like money and the entire point
 * of the unit is that it isn't.
 */
export function formatCredits(credits: number): string {
  return credits.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(credits) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** Signed credits for an activity row: `+5`, `-0.90`, `0`. */
export function formatSignedCredits(amountCredits: number): string {
  const abs = formatCredits(Math.abs(amountCredits));
  if (amountCredits > 0) return `+${abs}`;
  if (amountCredits < 0) return `-${abs}`;
  return abs;
}
